import { TrashIcon } from '@radix-ui/react-icons'
import { message } from 'antd'
import { ConfirmDeletePopup } from 'components/ConfirmDeletePopup'
import { ThumbnailImage } from 'features/assetDatabase/components/ThumbnailImage'
import { useRolodexEdit } from 'features/fde'
import { useRolodexEntries } from 'features/rolodex'
import React, { useState } from 'react'
import { GeckoEntry, GeckoReport } from 'types'

import { CoordImagePreviewer } from './CoordImagePreviewer'
import { FullImagePreviewer } from './FullImagePreviewer'
import { useImagesContext } from './ImagesContext'
import {
  ISDeleteIconContainer,
  ISImagesContainer,
  ISStarFilledIcon,
  ISStarIconContainer,
  ISStyledImage,
} from './StyledComponents'
import { generateGeometricPinQuery } from './utils'

interface ImagesRowProps {
  primaryImageIndex: number | null
  savePrimaryImage?: (index: number) => Promise<void>
  subSectionURLs: readonly string[]
  subSectionThumbnails: readonly GeckoReport[]
  componentID?: string
  inspectionID?: string
  onlyShowImageOnPopup?: boolean
}

export const ImagesRow: React.FC<ImagesRowProps> = ({
  primaryImageIndex,
  savePrimaryImage,
  subSectionURLs,
  subSectionThumbnails,
  componentID,
  inspectionID,
  onlyShowImageOnPopup,
}) => {
  const [showModal, setShowModal] = useState(false)
  const [selectedImageUrl, setSelectedImageUrl] = useState('')
  const [selectedImageThumbnail, setSelectedImageThumbnail] =
    useState<GeckoReport | null>(null)
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  )
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 })
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(
    null
  )

  const [messageApi, contextHolder] = message.useMessage()
  const { rolodexDelete } = useRolodexEdit()
  const { setImageVisibility, forceRefresh, imageVisibility } =
    useImagesContext()

  const { entries: geometricPins } = useRolodexEntries(
    generateGeometricPinQuery(
      pendingDeleteIndex,
      selectedImageIndex,
      subSectionThumbnails
    )
  )

  const handleImageError = (index: number) => {
    // Set the visibility of the broken image to false
    setImageVisibility((prevVisibility) => {
      const newVisibility = [...prevVisibility]
      newVisibility[index] = false
      return newVisibility
    })
  }

  const handleThumbnailClick = (url: string, index: number) => {
    setSelectedImageUrl(url)
    setSelectedImageThumbnail(subSectionThumbnails[index])
    setSelectedImageIndex(index)
    setShowModal(true)
  }

  const closeModal = async () => {
    setShowModal(false)
    setSelectedImageUrl('')
    setSelectedImageThumbnail(null)
    await forceRefresh()
  }

  const handleDeleteImage = async (
    index: number,
    geometricPins: readonly GeckoEntry[]
  ) => {
    if (subSectionThumbnails[index] !== undefined) {
      try {
        if (geometricPins.length > 0) {
          await Promise.all(
            geometricPins.map((pin) => rolodexDelete(pin.id, false))
          )
        }
        await rolodexDelete(subSectionThumbnails[index].id, true)
        messageApi.success('Image deleted successfully')
        await new Promise((resolve) => setTimeout(resolve, 1000)) // Wait 1 second
        await forceRefresh()
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred'
        messageApi.error(`Error deleting the image: ${errorMessage}`)
      }
    }
  }

  const confirmDelete = (e: React.MouseEvent, index: number) => {
    e.stopPropagation()
    handleDeleteImage(index, geometricPins)
    setPendingDeleteIndex(null)
  }

  const cancelDelete = () => {
    setPendingDeleteIndex(null)
  }

  const handleDeleteClick = (
    e: React.MouseEvent,
    index: number,
    setPopupPosition: React.Dispatch<
      React.SetStateAction<{ top: number; left: number }>
    >,
    setPendingDeleteIndex: React.Dispatch<React.SetStateAction<number | null>>
  ) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    setPopupPosition({
      top: rect.bottom + 8, // 8px below the delete icon
      left: rect.left + rect.width / 2, // Centered on delete icon
    })
    setPendingDeleteIndex(index)
  }

  return (
    <>
      {contextHolder}
      <ISImagesContainer>
        {subSectionURLs.map(
          (url, index) =>
            imageVisibility[index] &&
            url && (
              <ISStyledImage
                key={index}
                onClick={() => handleThumbnailClick(url, index)}>
                <ThumbnailImage
                  src={url}
                  alt={`Asset Thumbnail ${index + 1}`}
                  onError={() => handleImageError(index)}
                />
                {savePrimaryImage && (
                  <ISStarIconContainer
                    onClick={(e) => {
                      e.stopPropagation()
                      savePrimaryImage(index)
                    }}
                    $active={primaryImageIndex === index}>
                    <ISStarFilledIcon />
                  </ISStarIconContainer>
                )}

                <ISDeleteIconContainer
                  onClick={(e) =>
                    handleDeleteClick(
                      e,
                      index,
                      setPopupPosition,
                      setPendingDeleteIndex
                    )
                  }>
                  <TrashIcon />
                </ISDeleteIconContainer>
              </ISStyledImage>
            )
        )}
      </ISImagesContainer>
      {pendingDeleteIndex !== null && (
        <ConfirmDeletePopup
          entityName='image'
          popupPosition={popupPosition}
          cancelDelete={() => cancelDelete()}
          confirmDelete={(e) => confirmDelete(e, pendingDeleteIndex)}
        />
      )}
      {showModal &&
        selectedImageThumbnail &&
        (onlyShowImageOnPopup ? (
          <FullImagePreviewer
            imageUrl={selectedImageUrl}
            onClose={closeModal}
          />
        ) : (
          <CoordImagePreviewer
            imageUrl={selectedImageUrl}
            imageThumbnail={selectedImageThumbnail}
            onClose={closeModal}
            componentID={componentID}
            inspectionID={inspectionID}
            confirmDelete={confirmDelete}
            selectedImageIndex={selectedImageIndex}
          />
        ))}
    </>
  )
}
